import { useState } from "react";

import { IAddEmailResponse } from "@/Common/Api/AsyncActions/EmployeeAsyncActions";
import { ClientResponse } from "@/Common/Api/ClientResponse";
import { Modal } from "@/Common/Components/Modals/Modal";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { ISaveEmailOptions } from "@/Common/Models/Employee/ISaveEmailOptions";
import { ResourceProvider } from "@/Common/Resources/StaticResources";
import { Button } from "@/DesignSystem";
import { EmailInputSection } from "./EmailInputSection";

import styles from "./RecoveryEmailPopups.module.scss";

interface IRecoveryEmailSecondRequestPopupProps {
    emailOptions: ISaveEmailOptions;
    isWaitingForPassword: boolean;
    stringProvider?: ResourceProvider;
    showCloseButton?: boolean;

    onEmailChange: (emailAddress: string) => void;
    onNext: (emailOptions: ISaveEmailOptions) => Promise<ClientResponse<IAddEmailResponse>>;
    onIsOkToContactChanged: (checked: boolean) => void;
    onIsNotifiedByEmailChanged: (checked: boolean) => void;
    onClose?: () => void;
}

function RecoveryEmailSecondRequestPopup(props: IRecoveryEmailSecondRequestPopupProps): JSX.Element {
    const { getText } = useTranslation(props.stringProvider);

    const [isEmailValid, setIsEmailValid] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    const disableAddEmail: boolean =
        (sessionStore.isRecoveryEmailRequired && (!isEmailValid || IsNullOrWhiteSpace(props.emailOptions.emailAddress)))
        || isProcessing
        || props.isWaitingForPassword;

    async function next(): Promise<void> {
        if (disableAddEmail || props.isWaitingForPassword)
            return;

        setIsProcessing(true);
        const response = await props.onNext(props.emailOptions);
        if (!response.isSuccess)
            setError(response.errors?.[0]?.Message);
        setIsProcessing(false);
    }

    function renderFooter(): JSX.Element {
        return (
            <div className={styles.footerContainer}>
                <Button
                    variant="brand"
                    category="primary"
                    label={getText("NextLabel")}
                    disabled={disableAddEmail || props.isWaitingForPassword}
                    onClick={next}
                    dataCy="login-nextButton"
                />
            </div>
        );
    }

    return (
        <Modal
            title={getText("RecoveryEmailTitle")}
            showOverlay
            centerContent
            fixedOverlayPosition
            draggable
            showX={props.showCloseButton}
            disableOverlayClose
            footer={renderFooter()}
            dataCy="login-RequiredEmailSecondRequestPopup"
            onClose={props.onClose}
        >
            <div className={styles.requiredEmailPopupElements}>
                <strong className={styles.text}>{getText("PasswordRecoveryMessage")}</strong>
                <p className={styles.text}>{getText("PasswordRecoveryWarning")}</p>
                <p className={styles.text}>{getText("PasswordRecoveryAdvice")}</p>
                <EmailInputSection
                    error={error}
                    emailAddress={props.emailOptions.emailAddress}
                    isEmailValid={isEmailValid}
                    isOkToContact={props.emailOptions.isOkToContact}
                    isNotifiedByEmail={props.emailOptions.isNotifiedByEmail}
                    allowEmptyEmail={!sessionStore.isRecoveryEmailRequired}
                    stringProvider={props.stringProvider}
                    onEmailChange={(emailAddress, isEmailValid) => {
                        props.onEmailChange(emailAddress);
                        setIsEmailValid(isEmailValid);
                        setError(null);
                    }}
                    setIsOkToContact={props.onIsOkToContactChanged}
                    setIsNotifiedByEmail={props.onIsNotifiedByEmailChanged}
                    onEnterPressed={next}
                />
            </div>
        </Modal>
    );
}

export { RecoveryEmailSecondRequestPopup };
